import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Plus, X, Mail, Phone, Trash2, UserSquare, Building2, Filter, Download } from "lucide-react";
import { downloadCsv } from "@/lib/csv";
import { toast } from "sonner";

const ROLES = ["Cash Buyer", "Realtor", "Lender", "Contractor", "Property Manager", "Title/Escrow", "Insurance", "Other"];
const ROLE_COLOR = {
  "Cash Buyer": "#10B981",
  "Realtor": "#3B82F6",
  "Lender": "#8B5CF6",
  "Contractor": "#F59E0B",
  "Property Manager": "#E76F51",
  "Title/Escrow": "#06B6D4",
  "Insurance": "#EC4899",
  "Other": "#94A3B8",
};

const EMPTY = {
  name: "", role: "Cash Buyer", company: "", phone: "", email: "",
  markets: "", notes: "",
  buy_box_min_price: "", buy_box_max_price: "", buy_box_bedrooms: "", buy_box_property_types: "",
};

export default function Team() {
  const [items, setItems] = useState([]);
  const [roleFilter, setRoleFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    const url = roleFilter === "All" ? "/team" : `/team?role=${encodeURIComponent(roleFilter)}`;
    const { data } = await api.get(url);
    setItems(data);
  }, [roleFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    await api.delete(`/team/${id}`);
    fetchAll();
  };

  const onExport = () => {
    if (items.length === 0) {
      toast.error("Nothing to export.");
      return;
    }
    const columns = [
      { label: "name", accessor: (r) => r.name || "" },
      { label: "role", accessor: (r) => r.role || "" },
      { label: "company", accessor: (r) => r.company || "" },
      { label: "phone", accessor: (r) => r.phone || "" },
      { label: "email", accessor: (r) => r.email || "" },
      { label: "markets", accessor: (r) => r.markets || "" },
      { label: "buy_box_min_price", accessor: (r) => r.buy_box_min_price ?? "" },
      { label: "buy_box_max_price", accessor: (r) => r.buy_box_max_price ?? "" },
      { label: "buy_box_bedrooms", accessor: (r) => r.buy_box_bedrooms ?? "" },
      { label: "buy_box_property_types", accessor: (r) => r.buy_box_property_types || "" },
      { label: "notes", accessor: (r) => r.notes || "" },
      { label: "created_at", accessor: (r) => r.created_at || "" },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = roleFilter !== "All" ? `-${roleFilter.toLowerCase().replace(/[^a-z]/g, "-")}` : "";
    downloadCsv(`ispeedtoclose-power-team${suffix}-${stamp}.csv`, columns, items);
    toast.success(`Exported ${items.length} contact${items.length === 1 ? "" : "s"}`);
  };

  return (
    <div data-testid="team-page">
      <div className="topbar">
        <div>
          <div className="crumb">Power Team</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Power Team Directory</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">Cash buyers, contractors, lenders, agents, and PMs — all in one Rolodex.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onExport}
            data-testid="team-export-csv"
          >
            <Download size={14} /> Export
          </button>
          <button type="button" className="btn-money" onClick={() => { setEditing(null); setOpen(true); }} data-testid="team-add-button">
            <Plus size={14} /> Add Contact
          </button>
        </div>
      </div>

      <div className="card mb-5 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-[var(--ink-3)]" />
        {["All", ...ROLES].map((r) => (
          <button
            key={r}
            type="button"
            className={"px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors " +
              (roleFilter === r
                ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--ink)]")}
            onClick={() => setRoleFilter(r)}
            data-testid={`team-filter-${r.replace(/[^a-z]/gi, "").toLowerCase()}`}
          >
            {r === "All" ? "All" : r}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="empty" data-testid="team-empty">
          <UserSquare size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-semibold mb-1">No {roleFilter === "All" ? "team members" : roleFilter.toLowerCase() + "s"} yet</div>
          <div className="text-sm">Add the first contact to start building your power team.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((m) => (
            <TeamCard key={m.id} member={m} onEdit={() => { setEditing(m); setOpen(true); }} onDelete={() => onDelete(m.id)} />
          ))}
        </div>
      )}

      {open && <TeamModal initial={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); fetchAll(); }} />}
    </div>
  );
}

function TeamCard({ member, onEdit, onDelete }) {
  const color = ROLE_COLOR[member.role] || "#94A3B8";
  return (
    <div className="card relative group cursor-pointer hover:shadow-lg transition-shadow" onClick={onEdit} data-testid={`team-card-${member.id}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute top-3 right-3 btn-ghost opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 size={13} />
      </button>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center font-bold text-white" style={{ background: color }}>
          {member.name.slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg leading-tight truncate">{member.name}</h3>
          <div className="text-xs text-[var(--ink-2)] flex items-center gap-1 mt-0.5">
            <Building2 size={11} /> <span className="truncate">{member.company || "—"}</span>
          </div>
        </div>
      </div>
      <span className="chip mb-3" style={{ background: color + "14", color, borderColor: color + "55" }}>
        <span className="chip-dot" style={{ background: color }} /> {member.role}
      </span>
      <div className="space-y-1.5 text-sm">
        {member.phone && (
          <a href={`tel:${member.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 text-[var(--ink-2)] hover:text-[var(--ink)]">
            <Phone size={12} /> {member.phone}
          </a>
        )}
        {member.email && (
          <a href={`mailto:${member.email}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 text-[var(--ink-2)] hover:text-[var(--ink)] truncate">
            <Mail size={12} /> {member.email}
          </a>
        )}
      </div>
      {member.markets && (
        <div className="text-xs text-[var(--ink-2)] mt-3 pt-3 border-t border-[var(--line)]">
          <span className="font-semibold">Markets:</span> {member.markets}
        </div>
      )}
      {member.role === "Cash Buyer" && (member.buy_box_min_price || member.buy_box_max_price) && (
        <div className="text-xs text-[var(--ink-2)] mt-2">
          <span className="font-semibold">Buy box:</span> {fmtMoney(member.buy_box_min_price)} – {fmtMoney(member.buy_box_max_price)}
          {member.buy_box_bedrooms ? ` · ${member.buy_box_bedrooms}+ bed` : ""}
        </div>
      )}
    </div>
  );
}

function TeamModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => initial ? { ...EMPTY, ...initial } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isBuyer = form.role === "Cash Buyer";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const body = { ...form };
    ["buy_box_min_price", "buy_box_max_price", "buy_box_bedrooms"].forEach((k) => {
      body[k] = body[k] === "" || body[k] == null ? null : Number(body[k]);
    });
    try {
      if (initial) await api.patch(`/team/${initial.id}`, body);
      else await api.post("/team", body);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div>
            <div className="label-eyebrow">{initial ? "Edit contact" : "New contact"}</div>
            <h2 className="font-display font-bold text-xl mt-1">{initial?.name || "Add to your power team"}</h2>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid md:grid-cols-2 gap-4" data-testid="team-form">
          <div className="md:col-span-2">
            <label className="field-label">Name *</label>
            <input className="input" required value={form.name} onChange={set("name")} data-testid="team-name" />
          </div>
          <div>
            <label className="field-label">Role</label>
            <select className="select" value={form.role} onChange={set("role")} data-testid="team-role">
              {ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div><label className="field-label">Company</label><input className="input" value={form.company} onChange={set("company")} /></div>
          <div><label className="field-label">Phone</label><input className="input" value={form.phone} onChange={set("phone")} /></div>
          <div><label className="field-label">Email</label><input className="input" type="email" value={form.email} onChange={set("email")} /></div>
          <div className="md:col-span-2"><label className="field-label">Markets <span className="text-[var(--ink-2)] font-normal">(comma-separated)</span></label><input className="input" value={form.markets} onChange={set("markets")} placeholder="Austin, San Antonio" /></div>
          <div className="md:col-span-2"><label className="field-label">Notes</label><textarea className="textarea" rows={2} value={form.notes} onChange={set("notes")} /></div>

          {isBuyer && (
            <>
              <div className="md:col-span-2 divider my-2" />
              <div className="md:col-span-2 label-eyebrow">Buy box</div>
              <div><label className="field-label">Min price</label><input className="input" type="number" min="0" value={form.buy_box_min_price} onChange={set("buy_box_min_price")} /></div>
              <div><label className="field-label">Max price</label><input className="input" type="number" min="0" value={form.buy_box_max_price} onChange={set("buy_box_max_price")} /></div>
              <div><label className="field-label">Min bedrooms</label><input className="input" type="number" min="0" value={form.buy_box_bedrooms} onChange={set("buy_box_bedrooms")} /></div>
              <div><label className="field-label">Property types</label><input className="input" value={form.buy_box_property_types} onChange={set("buy_box_property_types")} placeholder="SFH, Duplex" /></div>
            </>
          )}

          {error && <div className="md:col-span-2 banner-err">{String(error)}</div>}
          <div className="md:col-span-2 flex justify-end gap-3 pt-3 border-t border-[var(--line)]">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-money" disabled={saving} data-testid="team-save-button">{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
